import { castArray, get, set, uniqueId } from 'lodash';
import { CONTACTS_FIELD_ENTITY } from 'src/views/Coversheet/constants/coverSheet';
import { getEmptyCardValues } from 'src/views/Coversheet/utils/cardUtils';
import { GQLCommissionType, type GQLCoverSheet } from '@/generated/graph-api';
import type { CoverSheetConfig } from '../configurations/types';

type ReplaceNullWithEmptyString<T> = T extends null
  ? ''
  : T extends (infer U)[]
    ? ReplaceNullWithEmptyString<U>[]
    : T extends object
      ? { [K in keyof T]: ReplaceNullWithEmptyString<T[K]> }
      : T;

// biome-ignore lint/suspicious/noExplicitAny: Preserve existing implementation
const convertAllNullsToEmptyString = <T extends Record<string | number, any>>(
  obj: T,
) => {
  if (!obj) return obj;
  // biome-ignore lint/suspicious/noExplicitAny: Preserve existing implementation
  const updated: Record<string | number, any> = { ...obj };
  Object.keys(obj).forEach((k) => {
    if (updated[k] === null) updated[k] = '';
    if (typeof updated[k] === 'object') {
      if (Array.isArray(updated[k])) {
        updated[k] = (updated[k] as unknown[]).map((item: unknown) =>
          // biome-ignore lint/suspicious/noExplicitAny: Preserve existing implementation
          convertAllNullsToEmptyString(item as Record<string, any>),
        );
      } else {
        updated[k] = convertAllNullsToEmptyString(
          // biome-ignore lint/suspicious/noExplicitAny: Preserve existing implementation
          updated[k] as Record<string, any>,
        );
      }
    }
  });

  return updated as ReplaceNullWithEmptyString<T>;
};

export type CoverSheetFormData = Omit<
  Partial<GQLCoverSheet>,
  typeof CONTACTS_FIELD_ENTITY
> & {
  [CONTACTS_FIELD_ENTITY]: Record<string, GQLCoverSheet['contacts']>;
};

export type SanitizedCoverSheetFormData =
  ReplaceNullWithEmptyString<CoverSheetFormData>;

export function transformCoverSheetToFormData(
  coverSheetData: Partial<GQLCoverSheet>,
  coverSheetConfig?: CoverSheetConfig,
  convertNullsToEmptyString?: true,
): SanitizedCoverSheetFormData;
export function transformCoverSheetToFormData(
  coverSheetData: Partial<GQLCoverSheet>,
  coverSheetConfig?: CoverSheetConfig,
  convertNullsToEmptyString?: false,
): CoverSheetFormData;
export function transformCoverSheetToFormData(
  coverSheetData: Partial<GQLCoverSheet>,
  coverSheetConfig?: CoverSheetConfig,
  convertNullsToEmptyString = true,
) {
  const dataContacts = coverSheetData[CONTACTS_FIELD_ENTITY] ?? [];

  let formData: CoverSheetFormData = {
    ...coverSheetData,
    [CONTACTS_FIELD_ENTITY]: dataContacts.reduce(
      (agg, contact) => {
        const contactType = contact.type ?? 'other';
        agg[contactType] ??= [];
        agg[contactType].push(contact);
        return agg;
      },
      {} as CoverSheetFormData['contacts'],
    ),
  };

  // Default commission type so amount field (percent/dollar) is visible when API returns null
  formData = {
    ...formData,
    ...(!formData.commissionType && {
      commissionType: GQLCommissionType.PERCENTAGE,
    }),
    ...(!formData.counterpartyCommissionType && {
      counterpartyCommissionType: GQLCommissionType.PERCENTAGE,
    }),
  };

  // Add addByDefault cards
  coverSheetConfig?.sections.forEach(({ cards }) => {
    cards.forEach((card) => {
      if (
        card.entity === CONTACTS_FIELD_ENTITY &&
        card.entityType &&
        (card.addByDefault || card.hideByDefault) &&
        castArray(get(formData, [CONTACTS_FIELD_ENTITY, card.entityType], []))
          .length < 1
      ) {
        formData = set(
          formData,
          [CONTACTS_FIELD_ENTITY, card.entityType],
          [{ ...getEmptyCardValues(card), rhfKey: uniqueId() }],
        );
      }
    });
  });

  return convertNullsToEmptyString
    ? convertAllNullsToEmptyString(formData)
    : formData;
}
